import React, { useCallback, useEffect, useState } from 'react';
import { View, ActivityIndicator } from 'react-native';
import { translate } from '../../../services/translate';
import MyAppText from '../../../components/MyAppText';
import Toast from 'react-native-toast-message';
import { cameraService } from '../../../services/central-api/cameras';
import { ClientError } from '../../../services/central-api/base-service';
import { ScrollView } from 'react-native-web-hover';
import AlertGroupsDropdown from '../../../components/Dropdowns/AlertGroupsDropdown';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import CustomButton from '../../../components/CustomButton';
import FormInput from '../../../components/FormInput';
import moment from 'moment';
import SilenceCameraModal from './SilenceCameraModal';
import Map from '../../../components/Map';
import { WindowInformation } from '../../../services/window-information';
import LocationSelector from '../../../components/LocationSelector';
import { centralAPI } from '../../../services/central-api';
import { useIsFocused, useFocusEffect, RouteProp, useRoute } from '@react-navigation/native';
import { useAuth } from '../../../context/Auth';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import { CamerasParamList } from '../../../typings/Params';
import CameraTagsDropdown from '../../../components/Filters/Cameras/CameraTagsDropdown';
import { RootNavigation } from '../../../Routes';
import { openInNewTab } from '../../../services/navigation-assistant';

interface CameraDetailsParameters {
    navigation: RootNavigation;
}

export default function CameraDetails({ navigation }: CameraDetailsParameters) {
    const { styles, theme } = useStyles(stylesheet);
    const { user: authenticatedUser } = useAuth();
    const route = useRoute<RouteProp<CamerasParamList, 'CameraDetails'>>();

    const windowInfo = WindowInformation();

    const [isLoading, setIsLoading] = useState(true);
    const [alreadyLoaded, setAlreadyLoaded] = useState(false);
    const [canEditFields, setCanEditFields] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [isModalVisible, setModalVisible] = useState<boolean>(false);

    // Third party cameras cannot be edited beside tags
    const [isThirdParty, setIsThirdParty] = useState<boolean>(false);

    // fields
    const [title, setTitle] = useState('');
    const [address, setAddress] = useState('');
    const [pin, setPin] = useState<LatLng>();
    const [cameraTags, setCameraTags] = useState<number[]>([]);
    const [selectedAlertGroupId, setSelectedAlertGroupId] = useState<number>(0);
    const [hasAreaInvasion, setHasAreaInvasion] = useState<boolean>();
    const [cameraSilencePeriod, setCameraSilencePeriod] = useState<CameraSilencePeriod>();

    async function getCamera(id: string) {
        try {
            setIsLoading(true);
            const camera = await cameraService.getCamera(id);
            setTitle(camera.title);
            fetchAddress({ lat: camera.latitude, lng: camera.longitude });
            setPin({ lat: camera.latitude, lng: camera.longitude });
            setCameraTags((camera.tags || []).map(t => t.id || 0));
            setIsThirdParty(camera.thirdPartyCamera || camera.originalCamera ? true : false);
            setSelectedAlertGroupId(camera.alertGroupId || 0);
            setHasAreaInvasion(camera.hasAreaInvasion);
            setCameraSilencePeriod(camera.cameraSilencePeriod);
        } catch (err) {
            console.error(err);
        } finally {
            setAlreadyLoaded(true);
            setIsLoading(false);
        }
    }

    async function fetchAddress(pos: LatLng) {
        const address = await centralAPI.getAddressByPlace({ latitude: pos.lat, longitude: pos.lng });
        if (address) {
            setAddress(address);
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            if (authenticatedUser) {
                setCanEditFields(authenticatedUser.isAdmin || authenticatedUser.permissions.camera_management);
            }
        }
    }, [isFocused, navigation, authenticatedUser]));

    useEffect(() => {
        if (route.params.id && !alreadyLoaded) {
            getCamera(route.params.id);
        }
    }, [route.params]);


    useEffect(() => {
        if (title === '') {
            return setIsFormValid(false);
        } else if (address === '') {
            return setIsFormValid(false);
        } else if (pin && Number.isNaN(Number(pin?.lat))) {
            return setIsFormValid(false);
        } else if (pin && Number.isNaN(Number(pin?.lng))) {
            return setIsFormValid(false);
        } else if (hasAreaInvasion && selectedAlertGroupId == 0) {
            return setIsFormValid(false);
        }
        return setIsFormValid(true);
    }, [title, address, pin, selectedAlertGroupId]);

    function cleanValues() {
        setAlreadyLoaded(false);
        setTitle('');
        setAddress('');
        setPin({ lat: 0, lng: 0 });
        setCameraTags([]);
        setSelectedAlertGroupId(0);
        setHasAreaInvasion(false);
        setCameraSilencePeriod({
            cameraId: '',
            silencedUntil: new Date(),
            user: {
                id: 0,
                name: ''
            },
            userId: 0
        });
    }

    function goBack() {
        navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Cameras', { screen: 'CamerasList' });
    }

    async function save() {
        try {
            setIsLoading(true);
            await cameraService.updateCamera(route.params.id, {
                title,
                address,
                latitude: pin ? Number(pin?.lat) : undefined,
                longitude: pin ? Number(pin?.lng) : undefined,
                tags: cameraTags,
                alertGroupId: selectedAlertGroupId !== 0 ? selectedAlertGroupId : undefined
            });
            cleanValues();
            navigation.navigate('Cameras', { screen: 'CamerasList' });
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <Container>
            <Header
                title={translate('camera')}
                goBackAction={() => {
                    cleanValues();
                    goBack();
                }}
                actions={<>
                    {!isThirdParty && (authenticatedUser?.permissions.silence_camera_events || authenticatedUser?.isAdmin) && hasAreaInvasion ?
                        <CustomButton
                            text={translate('silence')}
                            onPress={() => setModalVisible(true)}
                            disabled={cameraSilencePeriod && moment(cameraSilencePeriod.silencedUntil).isAfter(moment())}
                            type='secondary' />
                        : null
                    }
                    <CustomButton
                        type='secondary'
                        text={translate('watchCamera')}
                        onPress={() => {
                            openInNewTab('Cameras', { screen: 'CameraMapPlayer', params: { id: route.params.id } });
                        }} />
                    {canEditFields ?
                        <CustomButton
                            text={translate('save')}
                            onPress={save}
                            disabled={!isFormValid} />
                        : null
                    }
                </>}
            />
            <Card style={styles.customContent(windowInfo.isMobile)}>
                <ScrollView style={{ flex: 1, maxWidth: 400 }} contentContainerStyle={{ flex: 1, rowGap: 15, maxWidth: 400, width: '100%' }}>
                    {isLoading &&
                        <ActivityIndicator size='small' color={theme.colors.loadingColor} />
                    }
                    {isThirdParty ?
                        <MyAppText style={{ fontSize: 12 }}>{translate('thirdPartyCameraWarning')}</MyAppText> : null
                    }
                    {hasAreaInvasion && cameraSilencePeriod ?
                        <MyAppText style={{ fontSize: 12 }}>{translate('cameraSilencedText', {
                            silencedUntil: moment(cameraSilencePeriod.silencedUntil).format('DD/MM/YYYY HH:mm:ss'),
                            userName: cameraSilencePeriod.user.name
                        })}</MyAppText> : null
                    }
                    <View style={styles.generalInformationContainer}>
                        <FormInput
                            label={translate('title')}
                            placeholder=''
                            value={title}
                            disabled={!canEditFields || isThirdParty}
                            onChangeText={setTitle}
                            invalid={() => title === ''}
                            required={true}
                        />
                    </View>
                    <View style={[styles.generalInformationContainer, { zIndex: 10 }]}>
                        <LocationSelector
                            label={translate('address')}
                            address={address}
                            onAddressChange={setAddress}
                            onPinChange={pos => setPin(pos)}
                            disabled={!canEditFields || isThirdParty}
                            labelFontSize={14}
                            required={true}
                        />
                    </View>
                    <CameraTagsDropdown
                        value={cameraTags}
                        setValue={setCameraTags}
                        zIndex={3}
                        labelFontSize={14}
                    />

                    {(authenticatedUser?.permissions.manage_camera_alert_groups || authenticatedUser?.isAdmin) && hasAreaInvasion ?
                        <AlertGroupsDropdown
                            value={selectedAlertGroupId}
                            setValue={setSelectedAlertGroupId}
                            zIndex={2}
                            required={true}
                            labelFontSize={14}
                        />
                        : null
                    }
                </ScrollView>
                <View style={[!windowInfo.isMobile ? { flex: 2, width: '100%' } : { flexDirection: 'column', flex: 1 }]}>
                    <Map
                        dynamicMarker={pin}
                        useDynamicMarker={true}
                        draggable={canEditFields && !isThirdParty}
                        markerChanged={(pos) => {
                            fetchAddress(pos);
                            setPin(pos);
                        }}
                    />
                </View>
            </Card>
            <SilenceCameraModal
                isModalVisible={isModalVisible}
                setModalVisible={setModalVisible}
                cameraId={route.params.id}
                onConfirm={async () => await getCamera(route.params.id)} />
        </Container >
    );
}

const stylesheet = createStyleSheet(() => ({
    generalInformationContainer: {
        minHeight: 40,
    },
    customContent: (isMobile: boolean) => ({
        padding: 24,
        flex: 1,
        flexDirection: isMobile ? 'column' : 'row',
        gap: 20
    })
}));
